import { useEffect, useId, useRef } from 'react';

import { Button } from '@/components/Button';
import { useTranslation } from '@/i18n/useTranslation';
import { useKioskSession } from '@/lib/kioskSession';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

import { displayedUscisDocumentsUrl, uscisDocumentsUrl } from '../uscisDocuments';
import { UscisDocumentsQr } from './UscisDocumentsQr';

export interface DocumentsQrDialogProps {
  /** Close the overlay and put the candidate back on the I-9 step, exactly where they were. */
  readonly onClose: () => void;
}

/**
 * What *please click here* opens: a QR code the candidate scans with their OWN phone [decision 0370;
 * Figma · `03 · I-9 — USCIS documents QR … — Desktop` / `— iPad` / `— Mobile`, resolved by name on the
 * kiosk page and read live 2026-10-01].
 *
 * ⭐ **Nothing opens on the kiosk** — no tab, no page, no frame. The kiosk is not a browser [0302], and USCIS
 * forbids framing its page anyway (`frame-ancestors 'self' https://*.e-verify.gov https://*.dhs.gov`, 0370).
 * The candidate reads USCIS's list on a device that is theirs, so nothing is left open for the next person
 * and the session never leaves this app: the idle clear unmounts this overlay with the rest of the step
 * [0248], by construction.
 *
 * ⭐ **The code follows the language on screen**, read from the store on every render rather than captured
 * at the tap — a candidate on a Spanish kiosk gets USCIS's Spanish page, an English one the English page
 * [KAP-6, the Spanish-kiosk scenario].
 *
 * Values from the frame, identical at all three widths, and the same family as `ExitConfirmDialog`: a 327-wide
 * panel, 16px radius, 24px padding, 16px between blocks, `--elevation-3` over the 45% `--scrim`. The scan
 * block is the design system's `QR` component at its fixed 327, inside a 327 panel that clips — so it runs
 * edge to edge of the panel, which is what the frame renders; `-mx-6` reproduces that rather than shrinking
 * it to the 279 content column.
 *
 * ⚠️ **The copy is the frame's, and it is owed ISS's sign-off** [0040; the frame's own note: *"Overlay copy
 * is candidate-facing → ISS sign-off"*]. The visible URL is not copy: it is derived from the constant.
 *
 * **Not dismissible by an outside click**, following `ExitConfirmDialog`: a palm resting on a touchscreen
 * must not dismiss a dialog. Escape closes it — closing loses nothing.
 */
export function DocumentsQrDialog({ onClose }: DocumentsQrDialogProps) {
  const t = useTranslation();
  const language = useKioskSession((state) => state.language);
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const panel = useRef<HTMLDivElement | null>(null);
  const ids = useId();
  const titleId = `${ids}-title`;
  const bodyId = `${ids}-body`;
  const captionId = `${ids}-caption`;

  /*
   * The focus loop `ExitConfirmDialog` uses, at its smallest: `Close` is the one control here, so Tab and
   * Shift+Tab both stay on it rather than walking out onto the step behind the scrim.
   */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab' || panel.current === null) return;
      const close = panel.current.querySelector<HTMLElement>('button');
      if (close === null) return;
      event.preventDefault();
      close.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  return (
    /*
      `role="dialog"` and the labelling sit on the PANEL, not on the scrim — `ConsentDialog`'s and
      `NeedHelpDialog`'s arrangement. The scrim is a layout element, and a dialog whose box is the whole
      viewport tells a screen reader nothing about where it ends. The scrim is `--scrim`, the frame's own
      `Scrim (overlay exception)` layer at 45% black, the token both of those dialogs consume.
    */
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--scrim)] p-6">
      {/*
        `shadow:` is the type hint — see `KioskCard`. Without it the panel ships flat.

        Placement: the Desktop and Mobile frames put the panel 96px BELOW centre — Desktop y=328 where centre
        is 232 in 920, Mobile y=274 where centre is 178 in 812 (Figma 12885:8974 and 12885:9059, read live
        2026-10-06) — so `translate-y-24`. The `min-height: 720px` condition is this build's own choice, not
        the frame's: below it the offset would push the 456-tall panel off a short screen, so there it stays
        centred and never clips. The iPad frame is not followed: it draws the panel off-centre at x=137 of 820,
        the Exit overlay's artboard offset, which `design-qa/config.json` records as unusable.
      */}
      <div
        aria-describedby={bodyId}
        aria-labelledby={titleId}
        aria-modal="true"
        className="flex w-full max-w-[327px] flex-col items-center gap-4 overflow-hidden rounded-[var(--radius-lg)] bg-[var(--card)] p-6 shadow-[shadow:var(--elevation-3)] [@media(min-height:720px)]:translate-y-24"
        ref={panel}
        role="dialog"
      >
        <h2
          className="kiosk-heading-focus w-full text-center text-base font-semibold leading-6 text-[var(--foreground)]"
          id={titleId}
          ref={heading}
          tabIndex={-1}
        >
          {t('i9.documentsQr.title')}
        </h2>
        <p
          className="w-full text-center text-sm leading-5 text-[var(--muted-foreground)]"
          id={bodyId}
        >
          {t('i9.documentsQr.body')}
        </p>
        {/*
          The `QR` component: dashed 2px `--scan-border` (6 on, 6 off — the browser's own dash for a 2px
          border), 12px radius, 24px padding, 8px between the code and its caption. The stroke is INSIDE
          in Figma, so it takes no room; a CSS border does, so the padding is 22 and the block stays 188 tall.
        */}
        <figure className="-mx-6 flex w-[calc(100%+3rem)] flex-col items-center gap-2 rounded-[var(--radius-md)] border-2 border-dashed border-[var(--scan-border)] bg-[var(--scan-surface)] p-[22px]">
          <UscisDocumentsQr labelledBy={captionId} language={language} />
          <figcaption className="text-sm leading-5 text-[var(--scan-caption)]" id={captionId}>
            {t('i9.documentsQr.caption')}
          </figcaption>
        </figure>
        {/* Text, not a link — the frame's visible fallback for a phone that will not scan. */}
        <p className="w-full break-words text-center text-xs leading-5 text-[var(--muted-foreground)]">
          {displayedUscisDocumentsUrl(uscisDocumentsUrl(language))}
        </p>
        <Button fullWidth onClick={onClose}>
          {t('i9.documentsQr.close')}
        </Button>
      </div>
    </div>
  );
}
